import React, { useMemo, useState } from "react";
import { quiz } from "../data/quiz";

// =================== helpers ===================
const shuffle = (arr) => [...arr].sort(() => Math.random() - 0.5);

const getRandomQuestions = (count = 40) => {
  const shuffled = shuffle(quiz || []);
  return shuffled.slice(0, Math.min(count, shuffled.length));
};

const clamp = (n, a, b) => Math.max(a, Math.min(b, n));

// ✅ Default: si no viene type, asumimos mcq (esto arregla tu pantalla vacía)
const qType = (q) => (q?.type ? q.type : "mcq");

// =================== styles ===================
const PAGE_WRAP = {
  maxWidth: 980,
  margin: "4% auto 0",
  fontFamily: "Arial, sans-serif",
  minHeight: "95vh",
  padding: "0 16px 90px", // espacio para barra fija abajo
};

const CARD_STYLE = {
  background: "#ffffff",
  borderRadius: "22px",
  padding: "38px",
  margin: "28px auto 0",
  maxWidth: "920px",
  width: "min(920px, 96%)",
  minHeight: "360px",
  border: "1px solid rgba(15,23,42,0.10)",
  boxShadow: "0 18px 50px rgba(2, 6, 23, 0.12)",
};

const BTN_PRIMARY = {
  padding: "12px 26px",
  fontSize: 16,
  borderRadius: 12,
  background: "#0b5bd3",
  color: "#fff",
  border: "none",
  cursor: "pointer",
  fontWeight: 900,
  boxShadow: "0 10px 24px rgba(11,91,211,0.20)",
};

const BTN_DISABLED = {
  ...BTN_PRIMARY,
  background: "rgba(11,91,211,0.35)",
  cursor: "not-allowed",
  boxShadow: "none",
};

// =================== UI: Donut ===================
function Donut({ percent, size = 140, stroke = 16 }) {
  const p = clamp(percent, 0, 100);
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const dash = (p / 100) * c;

  return (
    <div style={{ display: "grid", placeItems: "center" }}>
      <svg width={size} height={size}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="rgba(0,0,0,0.10)"
          strokeWidth={stroke}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="#0b5bd3"
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={`${dash} ${c - dash}`}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>

      <div
        style={{
          marginTop: -size * 0.62,
          fontWeight: 900,
          fontSize: 22,
          color: "#0f172a",
        }}
      >
        {Math.round(p)}%
      </div>
    </div>
  );
}

// =================== UI: Bottom progress ===================
function BottomProgress({ currentIndex, total, answeredCount }) {
  const percent = total ? ((currentIndex + 1) / total) * 100 : 0;

  return (
    <div
      style={{
        position: "fixed",
        left: 0,
        right: 0,
        bottom: 0,
        padding: "10px 14px",
        background: "rgba(255,255,255,0.92)",
        borderTop: "1px solid rgba(17,24,39,0.10)",
        backdropFilter: "blur(10px)",
        zIndex: 50,
      }}
    >
      <div style={{ maxWidth: 980, margin: "0 auto", display: "grid", gap: 8 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            fontSize: 13,
            color: "#0f172a",
          }}
        >
          <span>
            Progreso: <b>{currentIndex + 1}</b> / {total}
          </span>
          <span>
            Contestadas: <b>{answeredCount}</b> / {total}
          </span>
        </div>

        <div
          style={{
            height: 10,
            borderRadius: 999,
            background: "rgba(0,0,0,0.10)",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              height: "100%",
              width: `${clamp(percent, 0, 100)}%`,
              background: "#0b5bd3",
              borderRadius: 999,
              transition: "width 180ms ease",
            }}
          />
        </div>
      </div>
    </div>
  );
}

// =================== Drag blanks question ===================
function DragBlanksQuestion({ q, value, onChange, disabled }) {
  // value: { [itemIndex]: string[] }
  const selectedByItem = value || {};

  const used = new Set();
  (q.items || []).forEach((it, itemIdx) => {
    const picks = selectedByItem[itemIdx] || [];
    picks.forEach((s) => s && used.add(s));
  });

  const remaining = (q.options || []).filter((o) => !used.has(o));

  const setPick = (itemIdx, blankIdx, option) => {
    const prev = selectedByItem[itemIdx] || [];
    const next = [...prev];
    next[blankIdx] = option;
    onChange({ ...selectedByItem, [itemIdx]: next });
  };

  const clearPick = (itemIdx, blankIdx) => {
    const prev = selectedByItem[itemIdx] || [];
    const next = [...prev];
    next[blankIdx] = "";
    onChange({ ...selectedByItem, [itemIdx]: next });
  };

  const allowDrop = (e) => e.preventDefault();

  const onDropToBlank = (e, itemIdx, blankIdx) => {
    e.preventDefault();
    if (disabled) return;
    const option = e.dataTransfer.getData("text/plain");
    if (!option) return;
    setPick(itemIdx, blankIdx, option);
  };

  return (
    <div style={{ display: "grid", gap: 16 }}>
      <div
        style={{
          background: "#0b2f67",
          color: "#fff",
          padding: "14px 16px",
          borderRadius: 14,
          fontWeight: 900,
        }}
      >
        {q.title || "Completa arrastrando la respuesta correcta."}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "260px 1fr", gap: 16, alignItems: "start" }}>
        {/* Pool */}
        <div
          style={{
            border: "1px solid rgba(17,24,39,0.12)",
            borderRadius: 14,
            background: "#fff",
            padding: 14,
          }}
        >
          <div style={{ fontWeight: 900, marginBottom: 10, color: "#0f172a" }}>Opciones</div>

          <div style={{ display: "grid", gap: 8 }}>
            {remaining.map((opt) => (
              <div
                key={opt}
                draggable={!disabled}
                onDragStart={(e) => e.dataTransfer.setData("text/plain", opt)}
                style={{
                  padding: "10px 12px",
                  borderRadius: 12,
                  border: "1px solid rgba(17,24,39,0.12)",
                  cursor: disabled ? "default" : "grab",
                  background: "#f8fbff",
                  fontWeight: 800,
                  color: "#0f172a",
                }}
                title={disabled ? "" : "Arrastra"}
              >
                {opt}
              </div>
            ))}

            {!remaining.length && (
              <div style={{ fontSize: 13, color: "#64748b" }}>
                Ya usaste todas las opciones. Puedes hacer clic en un hueco para quitarlo.
              </div>
            )}
          </div>
        </div>

        {/* Items */}
        <div style={{ display: "grid", gap: 12 }}>
          {(q.items || []).map((it, itemIdx) => {
            const parts = it.textParts || [];
            const blanks = Math.max(0, parts.length - 1);
            const picks = selectedByItem[itemIdx] || Array(blanks).fill("");

            return (
              <div
                key={itemIdx}
                style={{
                  border: "1px solid rgba(17,24,39,0.12)",
                  borderRadius: 14,
                  background: "#fff",
                  padding: 14,
                }}
              >
                <div style={{ fontWeight: 900, marginBottom: 8, color: "#0f172a" }}>
                  {itemIdx + 1}.
                </div>

                <div style={{ fontSize: 16, lineHeight: 1.6, color: "#0f172a" }}>
                  {parts.map((part, pi) => {
                    const isBlank = pi < parts.length - 1;

                    return (
                      <React.Fragment key={pi}>
                        {part}
                        {isBlank && (
                          <span
                            onDragOver={allowDrop}
                            onDrop={(e) => onDropToBlank(e, itemIdx, pi)}
                            onClick={() => (!disabled ? clearPick(itemIdx, pi) : null)}
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              minWidth: 170,
                              padding: "9px 10px",
                              margin: "0 8px",
                              borderRadius: 12,
                              border: "2px dashed rgba(11,91,211,0.50)",
                              background: picks[pi] ? "#e8f3ff" : "#f7fbff",
                              fontWeight: 900,
                              color: "#0b2f67",
                              cursor: disabled ? "default" : picks[pi] ? "pointer" : "copy",
                              userSelect: "none",
                              verticalAlign: "baseline",
                            }}
                            title={disabled ? "" : picks[pi] ? "Click para quitar" : "Suelta aquí"}
                          >
                            {picks[pi] || "— suelta aquí —"}
                          </span>
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div style={{ fontSize: 12, color: "#64748b" }}>
        Tip: clic en un hueco lleno para quitarlo y devolver la opción al banco.
      </div>
    </div>
  );
}

// =================== grading ===================
function gradeQuestion(q, userAnswer) {
  const type = qType(q);

  if (type === "mcq") {
    const ok = userAnswer === q.answer;
    const correctText = q.options?.[q.answer];
    const userText =
      typeof userAnswer === "number" ? q.options?.[userAnswer] : "(sin respuesta)";
    return { ok, correctText, userText };
  }

  if (type === "drag_blanks") {
    let totalBlanks = 0;
    let correctBlanks = 0;

    (q.items || []).forEach((it, itemIdx) => {
      const blanks = (it.textParts?.length || 0) - 1;
      totalBlanks += blanks;

      const picks = (userAnswer?.[itemIdx] || []).slice(0, blanks);

      for (let bi = 0; bi < blanks; bi++) {
        const expected = it.answers?.[bi] || "";
        const got = picks?.[bi] || "";
        if (expected && got && expected === got) correctBlanks++;
      }
    });

    const ok = totalBlanks > 0 && correctBlanks === totalBlanks;
    return { ok, correctBlanks, totalBlanks };
  }

  return { ok: false };
}

// =================== main component ===================
const QuizApp = () => {
  const [questions, setQuestions] = useState(getRandomQuestions(40));
  const [answers, setAnswers] = useState({}); // { [index]: number | dragObj }
  const [current, setCurrent] = useState(0);
  const [showResults, setShowResults] = useState(false);
  const [score, setScore] = useState(0);

  const [formVisible, setFormVisible] = useState(false);
  const [formData, setFormData] = useState({ name: "", email: "", score: 0 });
  const [submitted, setSubmitted] = useState(false);

  const answeredCount = useMemo(() => {
    let c = 0;
    (questions || []).forEach((q, i) => {
      const type = qType(q);
      const a = answers[i];

      if (type === "mcq") {
        if (typeof a === "number") c++;
      } else if (type === "drag_blanks") {
        const any =
          a &&
          Object.values(a).some(
            (arr) => Array.isArray(arr) && arr.some((x) => x)
          );
        if (any) c++;
      }
    });
    return c;
  }, [answers, questions]);

  const handleOptionSelect = (qIndex, optionIndex) => {
    if (showResults) return;
    setAnswers((prev) => ({ ...prev, [qIndex]: optionIndex }));
  };

  const handleDragAnswerChange = (qIndex, nextValue) => {
    if (showResults) return;
    setAnswers((prev) => ({ ...prev, [qIndex]: nextValue }));
  };

  const canGoNext = useMemo(() => {
    const q = questions[current];
    const a = answers[current];
    if (!q) return false;

    const type = qType(q);

    if (type === "mcq") return typeof a === "number";

    if (type === "drag_blanks") {
      let total = 0;
      let filled = 0;

      (q.items || []).forEach((it, itemIdx) => {
        const blanks = (it.textParts?.length || 0) - 1;
        total += blanks;
        const picks = a?.[itemIdx] || [];
        for (let bi = 0; bi < blanks; bi++) {
          if (picks[bi]) filled++;
        }
      });

      return total > 0 && filled === total;
    }

    return false;
  }, [answers, current, questions]);

  const handleNext = () => {
    if (current < questions.length - 1) {
      setCurrent((c) => c + 1);
      return;
    }

    let correctCount = 0;
    questions.forEach((q, i) => {
      const g = gradeQuestion(q, answers[i]);
      if (g.ok) correctCount++;
    });

    setScore(correctCount);
    setShowResults(true);
    setFormData((fd) => ({ ...fd, score: correctCount }));
  };

  const handleRetry = () => {
    setQuestions(getRandomQuestions(40));
    setAnswers({});
    setCurrent(0);
    setShowResults(false);
    setScore(0);
    setFormVisible(false);
    setFormData({ name: "", email: "", score: 0 });
    setSubmitted(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((fd) => ({ ...fd, [name]: value }));
  };

  const handleSubmitForm = (e) => {
    e.preventDefault();
    setSubmitted(true);
  };

  const renderForm = () => (
    <form onSubmit={handleSubmitForm} style={{ marginTop: 18 }}>
      <div style={{ marginBottom: 10 }}>
        <label style={{ fontWeight: 800, color: "#0f172a" }}>
          Nombre:
          <br />
          <input
            type="text"
            name="name"
            required
            value={formData.name}
            onChange={handleChange}
            style={{
              width: "100%",
              padding: 10,
              borderRadius: 10,
              border: "1px solid rgba(15,23,42,0.18)",
              marginTop: 6,
            }}
          />
        </label>
      </div>

      <div style={{ marginBottom: 10 }}>
        <label style={{ fontWeight: 800, color: "#0f172a" }}>
          Correo:
          <br />
          <input
            type="email"
            name="email"
            required
            value={formData.email}
            onChange={handleChange}
            style={{
              width: "100%",
              padding: 10,
              borderRadius: 10,
              border: "1px solid rgba(15,23,42,0.18)",
              marginTop: 6,
            }}
          />
        </label>
      </div>

      <div style={{ marginBottom: 12, color: "#0f172a", fontWeight: 900 }}>
        Nota: <span style={{ marginLeft: 8 }}>{formData.score}</span>
      </div>

      <button type="submit" style={BTN_PRIMARY} disabled={submitted}>
        Enviar
      </button>

      {submitted && (
        <p style={{ color: "green", marginTop: 10, fontWeight: 800 }}>
          Enviado correctamente.
        </p>
      )}
    </form>
  );

  const renderQuestion = (q, i) => {
    const type = qType(q);

    return (
      <div style={CARD_STYLE}>
        <div
          style={{
            marginBottom: 18,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 10,
            flexWrap: "wrap",
          }}
        >
          <strong style={{ fontSize: 22, color: "#0f172a" }}>
            Pregunta {i + 1} de {questions.length}
          </strong>

          <span style={{ fontSize: 13, color: "#64748b", fontWeight: 800 }}>
            Tipo: <b>{type === "drag_blanks" ? "Arrastrar" : "Opción múltiple"}</b>
          </span>
        </div>

        {/* ✅ MCQ por default */}
        {type === "mcq" && (
          <>
            <p style={{ fontSize: 18, marginBottom: 18, color: "#0f172a", fontWeight: 800 }}>
              {q.question}
            </p>

            <ul style={{ listStyle: "none", paddingLeft: 0, margin: 0 }}>
              {(q.options || []).map((opt, oi) => (
                <li key={oi} style={{ marginBottom: 10 }}>
                  <label
                    style={{
                      cursor: "pointer",
                      background: answers[i] === oi ? "#e8f3ff" : "#fff",
                      border:
                        answers[i] === oi
                          ? "2px solid #2f7ddb"
                          : "1px solid rgba(15,23,42,0.14)",
                      padding: "10px 14px",
                      borderRadius: 12,
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      fontSize: 16,
                      width: "100%",
                      transition: "all 0.2s",
                    }}
                  >
                    <input
                      type="radio"
                      name={`question-${i}`}
                      value={oi}
                      checked={answers[i] === oi}
                      onChange={() => handleOptionSelect(i, oi)}
                      disabled={showResults}
                    />
                    <span style={{ color: "#0f172a", fontWeight: 700 }}>
                      {opt}
                    </span>
                  </label>
                </li>
              ))}
            </ul>
          </>
        )}

        {/* Drag */}
        {type === "drag_blanks" && (
          <DragBlanksQuestion
            q={q}
            value={answers[i]}
            onChange={(v) => handleDragAnswerChange(i, v)}
            disabled={showResults}
          />
        )}

        <div style={{ marginTop: 22, display: "flex", justifyContent: "flex-end" }}>
          <button onClick={handleNext} disabled={!canGoNext} style={canGoNext ? BTN_PRIMARY : BTN_DISABLED}>
            {i === questions.length - 1 ? "Ver resultado final" : "Siguiente"}
          </button>
        </div>
      </div>
    );
  };

  const percent = questions.length ? (score / questions.length) * 100 : 0;

  const renderReview = () => (
    <div style={{ marginTop: 18, display: "grid", gap: 12 }}>
      {questions.map((q, i) => {
        const type = qType(q);
        const g = gradeQuestion(q, answers[i]);

        if (type === "mcq") {
          const ok = g.ok;
          const userText = g.userText || "(sin respuesta)";
          const correctText = g.correctText || "(sin respuesta correcta)";

          return (
            <div
              key={q.id || i}
              style={{
                border: "1px solid rgba(15,23,42,0.10)",
                borderRadius: 16,
                background: "#fff",
                padding: 14,
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                <div style={{ fontWeight: 900, color: "#0f172a" }}>
                  {i + 1}. {q.question}
                </div>
                <div style={{ fontWeight: 900, color: ok ? "#0a7a2f" : "#b00020" }}>
                  {ok ? "✅ Correcta" : "❌ Incorrecta"}
                </div>
              </div>

              <div style={{ marginTop: 10, fontSize: 14, color: "#0f172a" }}>
                <div>
                  <b>Tu respuesta:</b> {userText}
                </div>
                <div style={{ marginTop: 6 }}>
                  <b>Respuesta correcta:</b> {correctText}
                </div>
                {q.explanation && (
                  <div style={{ marginTop: 6, color: "#334155" }}>
                    <b>Explicación:</b> {q.explanation}
                  </div>
                )}
              </div>
            </div>
          );
        }

        if (type === "drag_blanks") {
          return (
            <div
              key={q.id || i}
              style={{
                border: "1px solid rgba(15,23,42,0.10)",
                borderRadius: 16,
                background: "#fff",
                padding: 14,
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                <div style={{ fontWeight: 900, color: "#0f172a" }}>
                  {i + 1}. {q.title || "Arrastrar"}
                </div>
                <div style={{ fontWeight: 900, color: g.ok ? "#0a7a2f" : "#b00020" }}>
                  {g.ok ? "✅ Correcta" : "❌ Incorrecta"}{" "}
                  <span style={{ color: "#64748b", fontWeight: 800, marginLeft: 8 }}>
                    ({g.correctBlanks}/{g.totalBlanks})
                  </span>
                </div>
              </div>

              <div style={{ marginTop: 10, display: "grid", gap: 10 }}>
                {(q.items || []).map((it, itemIdx) => {
                  const blanks = (it.textParts?.length || 0) - 1;
                  const picks = (answers[i]?.[itemIdx] || []).slice(0, blanks);

                  return (
                    <div
                      key={itemIdx}
                      style={{
                        padding: 10,
                        borderRadius: 14,
                        background: "#f8fbff",
                        border: "1px solid rgba(15,23,42,0.08)",
                      }}
                    >
                      <div style={{ fontWeight: 900, marginBottom: 6, color: "#0f172a" }}>
                        {itemIdx + 1}.
                      </div>

                      <div style={{ fontSize: 14, color: "#0f172a", lineHeight: 1.6 }}>
                        {(it.textParts || []).map((part, pi) => {
                          const isBlank = pi < (it.textParts || []).length - 1;
                          const got = picks[pi] || "(vacío)";
                          const expected = it.answers?.[pi] || "(sin answer)";
                          const isOk = got === expected;

                          return (
                            <React.Fragment key={pi}>
                              {part}
                              {isBlank && (
                                <span
                                  style={{
                                    display: "inline-block",
                                    margin: "0 6px",
                                    padding: "4px 8px",
                                    borderRadius: 12,
                                    border: "1px solid rgba(15,23,42,0.15)",
                                    background: "#fff",
                                  }}
                                >
                                  <span style={{ fontWeight: 900, color: isOk ? "#0a7a2f" : "#b00020" }}>
                                    {got} {isOk ? "✅" : "❌"}
                                  </span>
                                  <span style={{ marginLeft: 8, color: "#334155" }}>
                                    <b>Correcta:</b> {expected}
                                  </span>
                                </span>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        }

        return null;
      })}
    </div>
  );

  return (
    <div style={PAGE_WRAP}>
      <h2 style={{ margin: "22px 0 0", color: "#0b2f67", textAlign: "center", fontWeight: 900 }}>
        Quiz: Blockchain y Criptomonedas
      </h2>

      {!showResults && questions[current] && renderQuestion(questions[current], current)}

      {showResults && (
        <div style={CARD_STYLE}>
          <h3
            style={{
              fontSize: 22,
              color: "#0b2f67",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 12,
              flexWrap: "wrap",
              marginTop: 0,
            }}
          >
            <span>
              Quiz finalizado <span role="img" aria-label="trophy">🏆</span>
            </span>
            <span style={{ fontSize: 14, color: "#0f172a", fontWeight: 900 }}>
              Nota: {score} / {questions.length}
            </span>
          </h3>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 200px", gap: 18, alignItems: "center", marginTop: 14 }}>
            <div style={{ display: "grid", gap: 10 }}>
              <div style={{ fontSize: 14, color: "#0f172a" }}>
                <b>Porcentaje:</b> {Math.round(percent)}%
              </div>

              <div style={{ height: 12, borderRadius: 999, background: "rgba(0,0,0,0.10)", overflow: "hidden" }}>
                <div style={{ height: "100%", width: `${clamp(percent, 0, 100)}%`, background: "#0b5bd3", borderRadius: 999 }} />
              </div>

              <div style={{ fontSize: 12, color: "#64748b" }}>
                Respuestas correctas: <b>{score}</b> / {questions.length}
              </div>
            </div>

            <Donut percent={percent} />
          </div>

          <div style={{ marginTop: 18 }}>
            {score >= 9 ? (
              <>
                {!formVisible ? (
                  <button onClick={() => setFormVisible(true)} style={BTN_PRIMARY}>
                    Completa el formulario para enviar tu nota
                  </button>
                ) : (
                  renderForm()
                )}
              </>
            ) : (
              <>
                <p style={{ color: "#b00020", marginTop: 18, fontWeight: 900 }}>
                  Necesitas al menos 9 respuestas correctas para enviar el formulario.
                </p>
                <button onClick={handleRetry} style={BTN_PRIMARY}>
                  Volver a intentar
                </button>
              </>
            )}
          </div>

          <div style={{ marginTop: 24 }}>
            <h4 style={{ margin: 0, color: "#0b2f67", fontWeight: 900 }}>
              Desglose de todas las preguntas
            </h4>
            <div style={{ fontSize: 12, color: "#64748b", marginTop: 6 }}>
              ✅/❌ + tu respuesta + la correcta (y explicación si existe).
            </div>
            {renderReview()}
          </div>
        </div>
      )}

      {!showResults && (
        <BottomProgress currentIndex={current} total={questions.length} answeredCount={answeredCount} />
      )}
    </div>
  );
};

export default QuizApp;
